import React from 'react';
import {reportBugOnce} from '@packages/utilities/reporter';
import {
  type Viewport,
  VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT,
} from '../viewport/viewport';
import {ActionCreator} from '@packages/utilities/create-action';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';

/**
 * Per-feature configuration that each open canvas feature must provide.
 * `StateType` is the Flux state the feature's selectors read from - usually `DesignerStores`
 */
export type OpenCanvasFeatureConfig<StateType> = {
  /**
   * Returns true when the feature implementing this config is using the open canvas
   */
  isActive: (state: StateType) => boolean;

  selectors: {
    /**
     * Returns the id of the frame that is currently active for this feature
     */
    selectActiveFrameId: (state: StateType) => string | null;
    /**
     * Returns the ids of the frames whose content is currently rendered.
     */
    selectRenderedFrameIds: (state: StateType) => readonly string[];
    /**
     * Returns the id of the frame that is currently selected by the user.
     * A "selected" frame shows selection chrome (green border, bold label)
     * and exposes frame-level settings in the right sidebar.
     */
    selectSelectedFrameId: (state: StateType) => string | null;
    /**
     * Returns the style sheet options for this feature
     */
    selectStyleSheetOptions: (
      state: StateType
    ) => OpenCanvasStyleSheetOptions | null;
  };

  actions: {
    /**
     * Creates an action to dispatch when a node within an inactive frame is clicked.
     */
    inactiveFrameNodeClicked: ActionCreator<
      string,
      {
        frameId: string;
        selectedNodeNativeIdPath: NativeId[];
      }
    >;
  };

  ui: {
    /**
     * A function used to render the on-canvas frames UI for this feature
     */
    renderFrames: RenderFramesFn;
    /**
     * A function used to render the right-sidebar Frame settings UI for this
     * feature.
     *
     * Optional for now: features whose settings UI still lives in
     * `/designer-flux` (e.g. Component Canvas) cannot be registered through
     * the config yet. Once those components move into their
     * `@packages/systems` packages, this field should become required.
     */
    renderFrameSettings?: RenderFrameSettingsFn;
  };

  /**
   * Optional viewport configuration for features that need to provide a default viewport position.
   */
  viewport?: {
    /**
     * Returns the default viewport position for this feature.
     * If frameWidth and viewportWidth are provided, the position is centered horizontally.
     * If not provided, returns a static default position.
     */
    getDefaultPosition: (
      frameWidth?: number,
      viewportWidth?: number,
      frameOffsetX?: number,
      scale?: number
    ) => Viewport;
    /**
     * Zoom level below which inactive/non-selected frame toolbars are hidden
     * for this feature. Features may lower this to keep frame chrome visible at
     * further-out zoom levels; falls back to the shared
     * `VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT` default when omitted.
     */
    toolbarVisibilityZoomLimit?: number;
  };
};

export type OpenCanvasStyleSheetOptions = {
  filterOpenCanvasBodyStyles?: boolean;
  bodyElementStyleTarget?: string;
};

/**
 * Map of feature keys to their configurations
 */
type FeatureMap<StateType> = Record<string, OpenCanvasFeatureConfig<StateType>>;

type RenderFramesFn = () => React.ReactNode;

type RenderFrameSettingsFn = () => React.ReactNode;

type OpenCanvasActionCreator<
  StateType,
  Key extends keyof OpenCanvasFeatureConfig<StateType>['actions'],
> = OpenCanvasFeatureConfig<StateType>['actions'][Key] | null;

/**
 * Feature-transparent selectors produced by `createOpenCanvasConfig`.
 * Callsites use these without knowing which feature is currently active.
 */
type OpenCanvasConfigAPI<StateType> = {
  /**
   * Returns true when any feature is using the open canvas
   */
  selectIsOpenCanvasActive: (state: StateType) => boolean;
  /**
   * Returns a selector that checks whether a specific registered feature is active.
   *
   * @example
   * ```ts
   * const isComponentCanvas = useDesignerSelector(OpenCanvas.selectIsFeatureActive('componentCanvas'));
   * ```
   */
  selectIsFeatureActive: (featureKey: string) => (state: StateType) => boolean;
  /**
   * Returns the id of the frame that is currently the active in the open canvas
   */
  selectActiveFrameId: (state: StateType) => string | null;
  /**
   * Returns the ids of the frames whose content is currently rendered.
   */
  selectRenderedFrameIds: (state: StateType) => readonly string[];
  /**
   * Returns the id of the frame that is currently selected in the open canvas
   */
  selectSelectedFrameId: (state: StateType) => string | null;
  /**
   * Returns the render function for the on-canvas frames UI
   */
  selectFramesRenderer: (state: StateType) => RenderFramesFn | null;
  /**
   * Returns the render function for the right-sidebar Frame settings UI of
   * the currently active feature, or null if no feature is active or the
   * active feature did not register one.
   */
  selectFrameSettingsRenderer: (
    state: StateType
  ) => RenderFrameSettingsFn | null;
  /**
   * Returns the style sheet options for the open canvas
   */
  selectStyleSheetOptions: (
    state: StateType
  ) => OpenCanvasStyleSheetOptions | null;
  /**
   * Returns the action creator for when a node within an inactive frame is clicked.
   * The action creator is defined for the currently active feature.
   * Returns `null` if the action creator is missing.
   */
  selectInactiveFrameNodeClickedActionCreator: (
    state: StateType
  ) => OpenCanvasActionCreator<StateType, 'inactiveFrameNodeClicked'>;
  /**
   * Returns the default viewport position for the active feature, or null if no feature is active.
   */
  getDefaultViewportPositionForFeature: (
    featureKey: string,
    frameWidth?: number,
    viewportWidth?: number,
    frameOffsetX?: number,
    scale?: number
  ) => Viewport | null;
  /**
   * Returns the zoom level below which inactive/non-selected frame toolbars are
   * hidden for the currently active feature, falling back to the shared
   * `VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT` default when no feature is active
   * or the active feature did not configure one.
   */
  selectToolbarVisibilityZoomLimit: (state: StateType) => number;
};

/**
 * Returned by `selectRenderedFrameIds` when no Open Canvas feature is active.
 * A shared module-level reference keeps the selector referentially stable for
 * `useDesignerSelector`, which compares by reference and would otherwise
 * re-render its consumers on every dispatch.
 */
const NO_RENDERED_FRAME_IDS: readonly string[] = [];

/**
 * Creates a set of feature-transparent selectors for the open canvas system.
 *
 * Each feature (e.g. Component Canvas, Multi-Context Canvas) provides its own `isActive` predicate
 * along with selectors that are specific to that feature, mapping state data to expected open canvas data.
 *
 * At runtime, the first feature whose `isActive` returns `true` is used. Only one feature is expected to be active at a time.
 *
 * The Open Canvas config and selectors are initialized at the top-level of the Designer application.
 *
 * @example
 * ```ts
 * const openCanvas = createOpenCanvasConfig({
 *   features: {
 *     componentCanvas: componentCanvasOpenCanvasConfig,
 *     multiContextCanvas: multiContextCanvasOpenCanvasConfig,
 *   },
 * });
 *
 * // Feature-transparent — callsite doesn't know which feature is active
 * const activeId = useDesignerSelector(openCanvas.selectActiveFrameId);
 *
 * // Feature-specific — check if a particular feature is active
 * const isComponentCanvas = useDesignerSelector(openCanvas.selectIsFeatureActive('componentCanvas'));
 * ```
 */
export const createOpenCanvasConfig = <StateType>(input: {
  features: FeatureMap<StateType>;
}): OpenCanvasConfigAPI<StateType> => {
  const {features} = input;
  const featureEntries = Object.entries(features);

  const _alwaysFalse = (_state: StateType): boolean => false;

  const _getConfigByFeatureKey = (featureKey: string) => {
    return features[featureKey];
  };

  const _getActiveFeatureKey = (state: StateType): string | null => {
    let activeKey: string | null = null;

    for (const [key, config] of featureEntries) {
      if (config.isActive(state)) {
        if (activeKey === null) {
          activeKey = key;
        } else {
          reportBugOnce(
            'MultipleOpenCanvasFeaturesActive',
            'Multiple Open Canvas features are active but expected only one to be active at a time',
            {
              team: 'design-experience',
              ticket: 'https://webflow.atlassian.net/browse/DEX-489',
              activeFeatures: [activeKey, key],
            }
          );
        }
      }
    }

    return activeKey;
  };

  const selectIsOpenCanvasActive = (state: StateType): boolean => {
    return Boolean(_getActiveFeatureKey(state));
  };

  const selectIsFeatureActive = (
    featureKey: string
  ): ((state: StateType) => boolean) => {
    return features[featureKey]?.isActive ?? _alwaysFalse;
  };

  const selectActiveFrameId = (state: StateType) => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config ? config.selectors.selectActiveFrameId(state) : null;
  };

  const selectRenderedFrameIds = (state: StateType): readonly string[] => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return NO_RENDERED_FRAME_IDS;

    const config = _getConfigByFeatureKey(featureKey);
    return config
      ? config.selectors.selectRenderedFrameIds(state)
      : NO_RENDERED_FRAME_IDS;
  };

  const selectSelectedFrameId = (state: StateType): string | null => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config ? config.selectors.selectSelectedFrameId(state) : null;
  };

  const selectStyleSheetOptions = (
    state: StateType
  ): OpenCanvasStyleSheetOptions | null => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config ? config.selectors.selectStyleSheetOptions(state) : null;
  };

  const selectFramesRenderer = (state: StateType) => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config ? config.ui.renderFrames : null;
  };

  const selectFrameSettingsRenderer = (state: StateType) => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config?.ui.renderFrameSettings ?? null;
  };

  const selectInactiveFrameNodeClickedActionCreator = (state: StateType) => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return null;

    const config = _getConfigByFeatureKey(featureKey);
    return config?.actions.inactiveFrameNodeClicked ?? null;
  };

  const getDefaultViewportPositionForFeature = (
    featureKey: string,
    frameWidth?: number,
    viewportWidth?: number,
    frameOffsetX?: number,
    scale?: number
  ): Viewport | null => {
    const config = _getConfigByFeatureKey(featureKey);
    return (
      config?.viewport?.getDefaultPosition(
        frameWidth,
        viewportWidth,
        frameOffsetX,
        scale
      ) ?? null
    );
  };

  const selectToolbarVisibilityZoomLimit = (state: StateType): number => {
    const featureKey = _getActiveFeatureKey(state);
    if (!featureKey) return VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT;

    const config = _getConfigByFeatureKey(featureKey);
    return (
      config?.viewport?.toolbarVisibilityZoomLimit ??
      VIEWPORT_ZOOM_TOOLBAR_VISIBILITY_LIMIT
    );
  };

  return {
    selectIsOpenCanvasActive,
    selectIsFeatureActive,
    selectActiveFrameId,
    selectRenderedFrameIds,
    selectSelectedFrameId,
    selectFramesRenderer,
    selectFrameSettingsRenderer,
    selectStyleSheetOptions,
    selectInactiveFrameNodeClickedActionCreator,
    getDefaultViewportPositionForFeature,
    selectToolbarVisibilityZoomLimit,
  };
};
